import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Warning, Trash, CircleNotch } from '@phosphor-icons/react';

export default function BulkDeleteConfirmDialog({ open, onOpenChange, entityName, count, fetchDependencies, onConfirmDelete }) {
  const [depData, setDepData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && fetchDependencies) {
      setLoading(true);
      fetchDependencies()
        .then(res => setDepData(res.data))
        .catch(() => setDepData(null))
        .finally(() => setLoading(false));
    }
  }, [open, fetchDependencies]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onConfirmDelete();
      onOpenChange(false);
    } catch {
      // error handled by parent
    } finally {
      setDeleting(false);
    }
  };

  const names = depData?.names || [];
  const deps = depData?.dependencies || [];
  const hasDeps = depData?.has_dependencies || false;
  const showNames = names.length <= 5;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#2B4B8C]/10 max-w-lg" aria-describedby="bulk-delete-desc">
        <DialogHeader>
          <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
            <Trash size={22} className="text-red-500" />
            Eliminar {count} {entityName}
          </DialogTitle>
          <DialogDescription id="bulk-delete-desc" className="text-[#2B4B8C]/60">
            Esta accion no se puede deshacer
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <CircleNotch size={24} className="animate-spin text-[#2B4B8C]/40" />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-[#2B4B8C]">
              Se eliminarán <strong>{count} {entityName}</strong>:
            </p>

            {showNames && names.length > 0 && (
              <div className="p-3 rounded-lg bg-[#f8fafc] border border-[#2B4B8C]/10 max-h-32 overflow-y-auto">
                <ul className="space-y-0.5">
                  {names.map((name, i) => (
                    <li key={i} className="text-sm text-[#2B4B8C]/70">- {name}</li>
                  ))}
                </ul>
              </div>
            )}

            {hasDeps && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-2">
                  <Warning size={20} className="text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-700">Registros asociados que serán eliminados:</p>
                    <ul className="mt-1 space-y-0.5">
                      {deps.map((dep, i) => (
                        <li key={i} className="text-sm text-amber-600">- {dep}</li>
                      ))}
                    </ul>
                    <p className="text-xs text-amber-500 mt-2">Todos serán eliminados permanentemente.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 border-[#2B4B8C]/10" data-testid="cancel-bulk-delete-btn">
                Cancelar
              </Button>
              <Button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 text-white hover:bg-red-600"
                data-testid="confirm-bulk-delete-btn"
              >
                {deleting ? 'Eliminando...' : `Eliminar ${count}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
